import { produce } from 'immer';
import { isEmpty } from 'lodash/fp';
import { extractError } from 'app/lib/error';
import { toStringFqn } from 'app/lib/utils';
import { LOAD_PROJECT } from 'app/store/projects/types.js';
import {
    LOAD_QUERIES_FOR_ENTITY,
    OPEN_EXPLORE,
    CLOSE_EXPLORE,
    PREVIEW_EXPLORATION,
    SELECT_CELL,
    CHOOSE_VIZ,
    INITIATE_EXPLORATION_DOWNLOAD,
    CREATE_QUERY,
    UPDATE_QUERY,
    DELETE_QUERY,
    EDIT_QUERY,
    CREATE_EXPLORE_TRANSFORM,
    UPDATE_EXPLORE_TRANSFORM,
    PROMOTE_EXPLORATION,
    COPY_EXPLORATIONS,
    SET_DIMENSION_FILTER_VALUES,
    RENAME_QUERY
} from './types.js';
import { ExploreMode } from './constants';

export const INITIAL_STATE = {
    byEntityId: {},
    isInitiatingDownload: false,
    isExploreOpen: false,
    exploreMode: null,
    isProcessing: false,
    isSaving: false,
    currentQueryUuid: null,
    currentEntityId: null,
    currentFqn: null,
    error: null,
    hasExplicitPreview: false
};

function ensureEntityEntryExists(draft, entityId) {
    if (!draft.byEntityId.hasOwnProperty(entityId)) {
        draft.byEntityId[entityId] = {};
    }

    return draft.byEntityId[entityId];
}

function ensureQueryEntryExists(draft, entityId, queryUuid) {
    const entity = ensureEntityEntryExists(draft, entityId);

    if (!entity.hasOwnProperty(queryUuid)) {
        entity[queryUuid] = {};
    }

    return entity[queryUuid];
}

export const exploreDataReducer = produce((draft, action) => {
    switch (action.type) {
        case LOAD_QUERIES_FOR_ENTITY.SUCCEEDED: {
            const entityId = action.params.transformationId ?? toStringFqn(action.params.tableFqn);
            const queries = action.payload.entries;

            draft.byEntityId[entityId] = {};

            for (const query of queries) {
                draft.byEntityId[entityId][query.id] = {
                    model: query.model,
                    sql: query.sql
                };
            }

            break;
        }
        case COPY_EXPLORATIONS: {
            draft.byEntityId[action.meta.viewUuid] = {};
            break;
        }
        case CREATE_QUERY.STARTED: {
            if (!action.meta?.keepOpen) {
                draft.isExploreOpen = false;
                draft.currentQueryUuid = null;
                draft.currentEntityId = null;
            }
            draft.isSaving = true;
            break;
        }

        case CREATE_QUERY.SUCCEEDED: {
            const { id, transformationId, model, sql, tableFqn } = action.params;
            const assetId = transformationId ?? toStringFqn(tableFqn);
            const queries = ensureEntityEntryExists(draft, assetId);
            queries[id] = {
                model,
                sql
            };
            draft.isSaving = false;
            break;
        }

        case UPDATE_QUERY.STARTED: {
            const { entityId, queryUuid } = action.meta;
            const { model, sql } = action.payload;
            const query = ensureQueryEntryExists(draft, entityId, queryUuid);

            query.model = model;
            query.sql = sql;
            if (!action.meta?.keepOpen) {
                draft.isExploreOpen = false;
                draft.currentQueryUuid = null;
                draft.currentEntityId = null;
            }
            draft.isSaving = true;

            break;
        }

        case RENAME_QUERY.STARTED: {
            const { model, id, transformationId, tableFqn } = action.payload;

            draft.byEntityId[transformationId ?? toStringFqn(tableFqn)][id].model.name = model.name;
            draft.isSaving = true;

            break;
        }

        case DELETE_QUERY.SUCCEEDED: {
            const { id } = action.params;
            const { entityId } = action.meta;
            const queries = draft.byEntityId[entityId];
            if (queries) {
                delete queries[id];
            }
            break;
        }

        case OPEN_EXPLORE: {
            draft.currentQueryUuid = action.payload.queryUuid ?? null;
            draft.exploreMode = action.payload.exploreMode ?? action.payload.dialogMode ?? null;
            draft.currentFqn = action.payload.fqn ?? null;
            draft.isExploreOpen = true;
            break;
        }

        case CLOSE_EXPLORE: {
            draft.currentQueryUuid = null;
            draft.currentEntityId = null;
            draft.currentFqn = null;
            draft.exploreMode = null;
            draft.isExploreOpen = false;
            draft.hasExplicitPreview = false;
            break;
        }

        case PREVIEW_EXPLORATION:
            draft.hasExplicitPreview = true;
            break;

        case SELECT_CELL: {
            const { entityId, queryUuid, columnIndex, rowIndex } = action.payload;
            draft.byEntityId[entityId][queryUuid].selectedCell = { rowIndex, columnIndex };
            break;
        }

        case CHOOSE_VIZ: {
            const { entityId, queryUuid, viz } = action.payload;
            if (draft.byEntityId[entityId][queryUuid]) {
                draft.byEntityId[entityId][queryUuid].currentViz = viz;
            }
            break;
        }

        case INITIATE_EXPLORATION_DOWNLOAD.STARTED:
            draft.isInitiatingDownload = true;
            break;

        case INITIATE_EXPLORATION_DOWNLOAD.ENDED:
            draft.isInitiatingDownload = false;
            break;

        case EDIT_QUERY: {
            const { entityId } = action.payload;
            draft.isExploreOpen = true;
            draft.exploreMode = ExploreMode.EDIT_QUERY;
            draft.currentEntityId = entityId;
            break;
        }

        case CREATE_EXPLORE_TRANSFORM.STARTED:
        case UPDATE_EXPLORE_TRANSFORM.STARTED:
            draft.isProcessing = true;
            draft.isSaving = true;
            draft.error = null;
            break;
        case PROMOTE_EXPLORATION.STARTED:
        case LOAD_QUERIES_FOR_ENTITY.STARTED:
            draft.isProcessing = true;
            draft.error = null;
            break;

        case CREATE_QUERY.FAILED:
            draft.isSaving = false;
            draft.error = extractError(action.payload);
            break;
        case UPDATE_QUERY.SUCCEEDED:
        case UPDATE_QUERY.FAILED:
        case RENAME_QUERY.SUCCEEDED:
        case RENAME_QUERY.FAILED:
            draft.isSaving = false;
            break;

        case CREATE_EXPLORE_TRANSFORM.FAILED:
        case CREATE_EXPLORE_TRANSFORM.REJECTED:
        case UPDATE_EXPLORE_TRANSFORM.FAILED:
        case UPDATE_EXPLORE_TRANSFORM.REJECTED: {
            draft.error = extractError(action.payload);
            break;
        }

        case CREATE_EXPLORE_TRANSFORM.ENDED:
        case UPDATE_EXPLORE_TRANSFORM.ENDED:
            draft.isProcessing = false;
            draft.isSaving = false;
            break;
        case PROMOTE_EXPLORATION.ENDED:
        case LOAD_QUERIES_FOR_ENTITY.ENDED:
            draft.isProcessing = false;
            break;

        case LOAD_PROJECT.STARTED:
            if (!draft.isExploreOpen) {
                draft.currentEntityId = null;
                draft.exploreMode = null;
            }
            break;

        case SET_DIMENSION_FILTER_VALUES: {
            const { entityId, queryUuid, name, values } = action.payload;

            draft.byEntityId[entityId][queryUuid].model.dimensionFilters ||= {};

            if (isEmpty(values)) {
                delete draft.byEntityId[entityId][queryUuid].model.dimensionFilters[name];
            } else {
                draft.byEntityId[entityId][queryUuid].model.dimensionFilters[name] = values;
            }

            break;
        }

        default:
            break;
    }
}, INITIAL_STATE);
